import { totalPagesFromCount } from "../../../shared/ui/Pagination/paginationModel";

export const FINANCE_HUB_PAGE_SIZE = 20;

export const FINANCE_HUB_LIFECYCLE_FILTERS = [
  { value: "", label: "Активні та закриті" },
  { value: "all", label: "Усі статуси" },
  { value: "pending", label: "Очікує" },
  { value: "in_progress", label: "В процесі" },
  { value: "closed", label: "Закриті" },
  { value: "cancelled", label: "Скасовані" },
];

export const FINANCE_HUB_FINANCE_STATE_FILTERS = [
  { value: "", label: "Усі" },
  { value: "overdue", label: "Прострочено" },
  { value: "unpaid", label: "Не сплачено" },
  { value: "partial", label: "Частково сплачено" },
  { value: "paid", label: "Сплачено" },
  { value: "overpaid", label: "Переплата" },
  { value: "unconfigured", label: "Не налаштовано" },
];

export const FINANCE_HUB_INCOME_TYPE_FILTERS = [
  { value: "", label: "Усі" },
  { value: "percent", label: "Відсоток" },
  { value: "fixed", label: "Фіксована" },
  { value: "unconfigured", label: "Не налаштовано" },
];

export const FINANCE_HUB_CURRENCY_FILTERS = [
  { value: "", label: "Усі валюти" },
  { value: "UAH", label: "UAH" },
  { value: "USD", label: "USD" },
  { value: "EUR", label: "EUR" },
];

export const FINANCE_HUB_ORDERING_OPTIONS = [
  { value: "", label: "За пріоритетом" },
  { value: "expected_payment_date", label: "Дата ↑" },
  { value: "-expected_payment_date", label: "Дата ↓" },
  { value: "updated_at", label: "Оновлено ↑" },
  { value: "-updated_at", label: "Оновлено ↓" },
];

export const FINANCE_HUB_STATUS_CHIPS = [
  { value: "overdue", label: "Прострочено", statusKey: "overdue" },
  { value: "unpaid", label: "Не сплачено", statusKey: "unpaid" },
  { value: "partial", label: "Частково сплачено", statusKey: "partial" },
  { value: "paid", label: "Сплачено", statusKey: "paid" },
  { value: "overpaid", label: "Переплата", statusKey: "overpaid" },
  { value: "unconfigured", label: "Не налаштовано", statusKey: "unconfigured" },
];

const ALLOWED_LIFECYCLES = new Set(
  FINANCE_HUB_LIFECYCLE_FILTERS.map((item) => item.value).filter(Boolean),
);
const ALLOWED_FINANCE_STATES = new Set(
  FINANCE_HUB_FINANCE_STATE_FILTERS.map((item) => item.value).filter(Boolean),
);
const ALLOWED_INCOME_TYPES = new Set(
  FINANCE_HUB_INCOME_TYPE_FILTERS.map((item) => item.value).filter(Boolean),
);
const ALLOWED_CURRENCIES = new Set(
  FINANCE_HUB_CURRENCY_FILTERS.map((item) => item.value).filter(Boolean),
);
const ALLOWED_ORDERINGS = new Set(
  FINANCE_HUB_ORDERING_OPTIONS.map((item) => item.value).filter(Boolean),
);

const CANONICAL_QUERY_KEYS = new Set([
  "q",
  "lifecycle",
  "finance_state",
  "income_type",
  "currency",
  "expected_from",
  "expected_to",
  "ordering",
  "page",
]);

// Partner-compensation keys must never survive on /finance.
const FORBIDDEN_PARTNER_KEYS = new Set([
  "compensation",
  "compensation_currency",
]);

const MAX_Q_LENGTH = 100;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export const parsePageParam = (raw) => {
  if (raw == null || raw === "") return 1;
  const text = String(raw);
  if (!/^[1-9]\d*$/.test(text)) return 1;
  const numeric = Number(text);
  if (!Number.isSafeInteger(numeric) || numeric <= 0) return 1;
  return numeric;
};

export const totalFinanceHubPages = (count, pageSize = FINANCE_HUB_PAGE_SIZE) =>
  totalPagesFromCount(count, pageSize);

export function normalizeFinanceHubSearchQuery(raw) {
  if (raw == null) return "";
  const text = String(raw).trim();
  if (!text) return "";
  return text.slice(0, MAX_Q_LENGTH);
}

function normalizeIsoDate(raw) {
  if (raw == null || raw === "") return "";
  const text = String(raw).trim();
  return ISO_DATE_RE.test(text) ? text : "";
}

export function readFinanceHubQueryParams(search) {
  const params = new URLSearchParams(
    typeof search === "string"
      ? search
      : search && typeof search.toString === "function"
        ? search.toString()
        : "",
  );
  const rawLifecycle = params.get("lifecycle") || "";
  const rawFinanceState = params.get("finance_state") || "";
  const rawIncomeType = params.get("income_type") || "";
  const rawCurrency = (params.get("currency") || "").toUpperCase();
  const rawOrdering = params.get("ordering") || "";

  return {
    page: parsePageParam(params.get("page")),
    q: normalizeFinanceHubSearchQuery(params.get("q")),
    lifecycle: ALLOWED_LIFECYCLES.has(rawLifecycle) ? rawLifecycle : "",
    finance_state: ALLOWED_FINANCE_STATES.has(rawFinanceState)
      ? rawFinanceState
      : "",
    income_type: ALLOWED_INCOME_TYPES.has(rawIncomeType) ? rawIncomeType : "",
    currency: ALLOWED_CURRENCIES.has(rawCurrency) ? rawCurrency : "",
    expected_from: normalizeIsoDate(params.get("expected_from")),
    expected_to: normalizeIsoDate(params.get("expected_to")),
    ordering:
      ALLOWED_ORDERINGS.has(rawOrdering) && rawOrdering !== "priority"
        ? rawOrdering
        : "",
  };
}

export function buildFinanceHubSearchParams({
  page = 1,
  q = "",
  lifecycle = "",
  finance_state = "",
  income_type = "",
  currency = "",
  expected_from = "",
  expected_to = "",
  ordering = "",
} = {}) {
  const next = new URLSearchParams();
  const normalizedQ = normalizeFinanceHubSearchQuery(q);
  if (normalizedQ) next.set("q", normalizedQ);
  if (lifecycle && ALLOWED_LIFECYCLES.has(lifecycle)) {
    next.set("lifecycle", lifecycle);
  }
  if (finance_state && ALLOWED_FINANCE_STATES.has(finance_state)) {
    next.set("finance_state", finance_state);
  }
  if (income_type && ALLOWED_INCOME_TYPES.has(income_type)) {
    next.set("income_type", income_type);
  }
  if (currency && ALLOWED_CURRENCIES.has(currency)) {
    next.set("currency", currency);
  }
  if (normalizeIsoDate(expected_from)) {
    next.set("expected_from", normalizeIsoDate(expected_from));
  }
  if (normalizeIsoDate(expected_to)) {
    next.set("expected_to", normalizeIsoDate(expected_to));
  }
  if (ordering && ALLOWED_ORDERINGS.has(ordering) && ordering !== "priority") {
    next.set("ordering", ordering);
  }
  if (page > 1) {
    next.set("page", String(page));
  }
  return next;
}

export function financeHubSearchNeedsCanonicalization(search) {
  const raw = new URLSearchParams(
    typeof search === "string"
      ? search
      : search && typeof search.toString === "function"
        ? search.toString()
        : "",
  );
  const canonical = buildFinanceHubSearchParams(readFinanceHubQueryParams(raw));

  for (const key of raw.keys()) {
    if (FORBIDDEN_PARTNER_KEYS.has(key)) return true;
    if (!CANONICAL_QUERY_KEYS.has(key)) return true;
  }

  return raw.toString() !== canonical.toString();
}

export function buildFinanceHubFetchParams({
  page = 1,
  q = "",
  lifecycle = "",
  finance_state = "",
  income_type = "",
  currency = "",
  expected_from = "",
  expected_to = "",
  ordering = "",
} = {}) {
  const params = {
    page,
    page_size: FINANCE_HUB_PAGE_SIZE,
  };
  const normalizedQ = normalizeFinanceHubSearchQuery(q);
  if (normalizedQ) params.q = normalizedQ;
  // Default lifecycle is server non_cancelled — omit empty/default from API.
  if (lifecycle && ALLOWED_LIFECYCLES.has(lifecycle)) {
    params.lifecycle = lifecycle;
  }
  if (finance_state && ALLOWED_FINANCE_STATES.has(finance_state)) {
    params.finance_state = finance_state;
  }
  if (income_type && ALLOWED_INCOME_TYPES.has(income_type)) {
    params.income_type = income_type;
  }
  if (currency && ALLOWED_CURRENCIES.has(currency)) {
    params.currency = currency;
  }
  if (normalizeIsoDate(expected_from)) {
    params.expected_from = normalizeIsoDate(expected_from);
  }
  if (normalizeIsoDate(expected_to)) {
    params.expected_to = normalizeIsoDate(expected_to);
  }
  if (ordering && ALLOWED_ORDERINGS.has(ordering)) {
    params.ordering = ordering;
  } else {
    params.ordering = "priority";
  }
  return params;
}

export function financeHubHasActiveFilters({
  q = "",
  lifecycle = "",
  finance_state = "",
  income_type = "",
  currency = "",
  expected_from = "",
  expected_to = "",
} = {}) {
  return Boolean(
    normalizeFinanceHubSearchQuery(q)
    || lifecycle
    || finance_state
    || income_type
    || currency
    || expected_from
    || expected_to,
  );
}

const filterOptionLabel = (options, value) => {
  const hit = options.find((item) => item.value === value);
  return hit ? hit.label : value;
};

/** Active advanced-filter chips for toolbar summary (excludes ordering). */
export function buildFinanceHubActiveFilterChips(query = {}) {
  const chips = [];
  const searchQ = normalizeFinanceHubSearchQuery(query.q);
  if (searchQ) {
    chips.push({
      key: "q",
      label: `Пошук: ${searchQ}`,
      clear: { q: "", page: 1 },
    });
  }
  if (query.lifecycle) {
    chips.push({
      key: "lifecycle",
      label: filterOptionLabel(FINANCE_HUB_LIFECYCLE_FILTERS, query.lifecycle),
      clear: { lifecycle: "", page: 1 },
    });
  }
  if (query.finance_state) {
    chips.push({
      key: "finance_state",
      label: filterOptionLabel(FINANCE_HUB_FINANCE_STATE_FILTERS, query.finance_state),
      clear: { finance_state: "", page: 1 },
    });
  }
  if (query.income_type) {
    chips.push({
      key: "income_type",
      label: filterOptionLabel(FINANCE_HUB_INCOME_TYPE_FILTERS, query.income_type),
      clear: { income_type: "", page: 1 },
    });
  }
  if (query.currency) {
    chips.push({
      key: "currency",
      label: filterOptionLabel(FINANCE_HUB_CURRENCY_FILTERS, query.currency),
      clear: { currency: "", page: 1 },
    });
  }
  const expectedFrom = normalizeIsoDate(query.expected_from);
  const expectedTo = normalizeIsoDate(query.expected_to);
  if (expectedFrom || expectedTo) {
    let rangeLabel = "";
    if (expectedFrom && expectedTo) {
      rangeLabel = `${expectedFrom} — ${expectedTo}`;
    } else if (expectedFrom) {
      rangeLabel = `від ${expectedFrom}`;
    } else {
      rangeLabel = `до ${expectedTo}`;
    }
    chips.push({
      key: "expected_dates",
      label: `Оплата: ${rangeLabel}`,
      clear: { expected_from: "", expected_to: "", page: 1 },
    });
  }
  return chips;
}

export function kyivTodayISO(now = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Kyiv",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}
